import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  useState,
  ReactNode,
  useCallback,
} from "react";
import cn from "classnames";
import { identity, pickBy } from "lodash-es";
import styled from "@emotion/styled";
import { ChevronRight } from "src/icons/ChevronRight";
import { Expandable } from "src/atoms/Expandable";
import BaseButton from "src/atoms/Button/BaseButton";

const StyledAccordion = styled.div<{ iconSize?: number }>`
  .Accordion__title {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    padding-bottom: 16px;

    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.tone.black};

    cursor: pointer;

    width: 100%;
    background-color: transparent;
    outline: none;
    border: none;
  }

  .Accordion__titleIcon {
    width: ${({ iconSize }) => iconSize || 24}px;
    height: ${({ iconSize }) => iconSize || 24}px;
    fill: ${({ theme }) => theme.colors.tone.black};
    transition: all 0.3s;
    pointer-events: none;
    transform: rotate(90deg);
  }

  .Accordion_contentContainer {
    padding-bottom: 16px;

    font-size: 14px;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.tone.black};
  }

  .Accordion__title_active > .Accordion__titleIcon {
    transform: rotate(-90deg);
  }
`;

type Icon = FC<{
  className?: string;
  isActive: boolean;
  size?: number;
}>;

interface Props {
  className?: string;
  expanded?: boolean;
  icon?: Icon;
  iconSize?: number;
  children: ReactNode;
}

const Accordion: FC<Props> & {
  Panel: typeof AccordionPanel;
  Title: typeof AccordionTitle;
  Content: typeof AccordionContent;
  Control: typeof Control;
} = ({ className, expanded = false, icon, iconSize, children }) => {
  return (
    <StyledAccordion className={className} iconSize={iconSize}>
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(
            child,
            pickBy(
              {
                expanded,
                icon,
                iconSize,
              },
              identity
            )
          );
        }

        return child;
      })}
    </StyledAccordion>
  );
};

interface ControlProps {
  onToggle: () => void;
  expanded?: boolean;
  isActive?: boolean;
  children: ({ onToggle }: { onToggle: () => void }) => ReactNode;
}
const Control: FC<ControlProps> = ({ onToggle, isActive, children }) => {
  if (!isActive) return null;
  return <div style={{ padding: "0 20px" }}>{children({ onToggle })}</div>;
};

interface AccordionPanelProps {
  className?: string;
  expanded?: boolean;
  icon?: Icon;
  iconSize?: number;
  children: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  onToggle?: (expanded: boolean) => void;
}

const AccordionPanel: FC<AccordionPanelProps> = ({
  className,
  expanded,
  icon,
  iconSize,
  children,
  onOpen,
  onClose,
  onToggle,
  ...rest
}) => {
  const [isActive, setIsActive] = useState(expanded);

  const handleTitleClick = useCallback(() => {
    setIsActive((currentValue) => {
      const newValue = !currentValue;

      if (newValue) {
        onOpen?.();
      } else {
        onClose?.();
      }

      onToggle?.(newValue);

      return newValue;
    });
  }, [onOpen, onClose, onToggle]);

  return (
    <div className={className}>
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement<any>(child, {
            isActive,
            onTitleClick: handleTitleClick,
            expanded,
            icon,
            iconSize,
            ...rest,
          });
        }

        return child;
      })}
    </div>
  );
};

interface AccordionTitleProps {
  className?: string;
  isActive?: boolean;
  icon?: Icon;
  iconSize?: number;
  onTitleClick?: () => void;
  children: ReactNode;
}

const AccordionTitle: FC<AccordionTitleProps> = ({
  className,
  isActive,
  onTitleClick,
  icon: TitleIcon,
  iconSize,
  children,
}) => {
  return (
    <BaseButton
      data-testid="accordion-title"
      className={cn(className, "Accordion__title", {
        Accordion__title_active: isActive,
      })}
      onPress={onTitleClick}
    >
      {children}
      {TitleIcon ? (
        <TitleIcon
          isActive={!!isActive}
          className="Accordion__titleIcon"
          size={iconSize}
        />
      ) : (
        <ChevronRight className="Accordion__titleIcon" />
      )}
    </BaseButton>
  );
};

interface AccordionContentProps {
  className?: string;
  isActive?: boolean;
  children: ReactNode;
}

const AccordionContent: FC<AccordionContentProps> = ({
  className,
  isActive,
  children,
}) => {
  return (
    <Expandable className={className} isExpanded={isActive}>
      <div className="Accordion_contentContainer">{children}</div>
    </Expandable>
  );
};

Accordion.Panel = AccordionPanel;
Accordion.Title = AccordionTitle;
Accordion.Content = AccordionContent;
Accordion.Control = Control;

export { Accordion };
